import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import {
  Radar, Plus, Loader2, Play, Square, Sparkles, ArrowRight, MessageSquare,
  Download, FileDown, Link2, Palette, Upload, Check, X,
} from "lucide-react";
import {
  siListSites, siCreateSite, siStartCrawl, siGetCrawl, siListCrawls,
  siCrawlIssues, siCancelCrawl, siSeoAnalystAsk, siDownloadReport,
  siGetBranding, siPutBranding, siBrandingLogoUrl, siShareReport,
} from "@/lib/api";
import { toast } from "sonner";

/** Search Intelligence authed console (`/si/console`). Phase 1 UI:
 *  add sites → start crawl → watch progress → review grouped issues →
 *  ask the SEO Analyst assistant grounded on the latest crawl. */

const scoreTone = (v) => (v >= 75 ? "emerald" : v >= 50 ? "amber" : "rose");
const TONE_MAP = {
  emerald: "bg-emerald-50 border-emerald-200 text-emerald-800",
  amber: "bg-amber-50 border-amber-200 text-amber-800",
  rose: "bg-rose-50 border-rose-200 text-rose-800",
};
const SEVERITY_MAP = {
  critical: "bg-rose-50 border-rose-200 text-rose-700",
  warning: "bg-amber-50 border-amber-200 text-amber-800",
  info: "bg-slate-50 border-slate-200 text-slate-700",
};

const ScoreBlock = ({ scores }) => {
  if (!scores) return null;
  const items = [
    ["Readiness", scores.readiness],
    ["Technical", scores.technical_health],
    ["Opportunity", scores.search_opportunity],
    ["Business", scores.business_alignment],
    ["Confidence", scores.data_confidence],
  ];
  return (
    <div className="grid grid-cols-2 gap-2 sm:grid-cols-5">
      {items.map(([l, v]) => (
        <div key={l} className={`rounded-lg border p-3 ${TONE_MAP[scoreTone(v)]}`}>
          <div className="text-[10px] font-semibold uppercase opacity-70">{l}</div>
          <div className="font-heading text-2xl font-black">{v}</div>
        </div>
      ))}
    </div>
  );
};

const SitePanel = ({ site, onRefresh }) => {
  const [crawls, setCrawls] = useState([]);
  const [activeCid, setActiveCid] = useState(null);
  const [progress, setProgress] = useState(null);
  const [issues, setIssues] = useState(null);
  const [starting, setStarting] = useState(false);
  const [ask, setAsk] = useState("");
  const [asking, setAsking] = useState(false);
  const [analyst, setAnalyst] = useState(null);
  const [whiteLabel, setWhiteLabel] = useState(false);
  const [downloading, setDownloading] = useState(null); // 'pdf' | 'csv' | null
  const [shareLink, setShareLink] = useState(null);     // { share_url, expires_at } | null
  const [sharing, setSharing] = useState(false);
  const [brandOpen, setBrandOpen] = useState(false);
  const [brand, setBrand] = useState({ brand_name: "", accent: "", has_logo: false, logo_url: null });
  const [brandBusy, setBrandBusy] = useState(false);
  const [logoFile, setLogoFile] = useState(null);

  const loadCrawls = useCallback(async () => {
    const r = await siListCrawls(site._id);
    setCrawls(r.crawls || []);
    const running = (r.crawls || []).find(c => ["queued", "running"].includes(c.status));
    if (running) setActiveCid(running._id);
    else if (r.crawls?.[0]) { setActiveCid(r.crawls[0]._id); }
  }, [site._id]);

  useEffect(() => { loadCrawls(); }, [loadCrawls]);

  // Poll progress
  useEffect(() => {
    if (!activeCid) return;
    let alive = true;
    const tick = async () => {
      try {
        const c = await siGetCrawl(site._id, activeCid);
        if (!alive) return;
        setProgress(c);
        if (c.status === "completed" || c.status === "cancelled") {
          const i = await siCrawlIssues(site._id, activeCid);
          if (alive) setIssues(i);
          onRefresh?.();
        } else {
          setTimeout(tick, 2500);
        }
      } catch (_) { /* ignore */ }
    };
    tick();
    return () => { alive = false; };
  }, [site._id, activeCid, onRefresh]);

  const startCrawl = async () => {
    setStarting(true);
    try {
      const r = await siStartCrawl(site._id, { limit: 25, concurrency: 3, obey_robots: true });
      setActiveCid(r.crawl_id);
      setProgress(null); setIssues(null);
      await loadCrawls();
      toast.success("Crawl queued");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start crawl");
    } finally { setStarting(false); }
  };

  const cancel = async () => {
    if (!activeCid) return;
    try { await siCancelCrawl(site._id, activeCid); toast.success("Cancelling…"); }
    catch (e) { toast.error("Cancel failed"); }
  };

  const askAnalyst = async () => {
    if (!ask.trim()) return;
    setAsking(true); setAnalyst(null);
    try {
      const r = await siSeoAnalystAsk(site._id, ask.trim());
      setAnalyst(r);
    } catch (e) { toast.error(e?.response?.data?.detail || "Assistant unavailable"); }
    finally { setAsking(false); }
  };

  const downloadReport = async (fmt) => {
    if (!activeCid) return;
    setDownloading(fmt);
    try {
      await siDownloadReport(site._id, activeCid, fmt, whiteLabel);
      toast.success(fmt === "csv" ? "CSV downloaded" : "PDF report downloaded");
    } catch (e) {
      toast.error(e?.response?.data?.detail || `Could not download ${fmt.toUpperCase()}`);
    } finally { setDownloading(null); }
  };

  const createShareLink = async () => {
    if (!activeCid) return;
    setSharing(true); setShareLink(null);
    try {
      const r = await siShareReport(site._id, activeCid,
        { white_label: whiteLabel, ttl_days: 30 });
      setShareLink(r);
      try {
        await navigator.clipboard.writeText(r.share_url);
        toast.success("Share link copied to clipboard");
      } catch { toast.success("Share link created"); }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not create share link");
    } finally { setSharing(false); }
  };

  const loadBranding = useCallback(async () => {
    try {
      const b = await siGetBranding(site._id);
      setBrand({
        brand_name: b.brand_name || "",
        accent: b.accent || "",
        has_logo: !!b.has_logo,
        logo_url: b.logo_url ? `${siBrandingLogoUrl(site._id)}?t=${Date.now()}` : null,
      });
    } catch { /* ignore */ }
  }, [site._id]);

  const saveBranding = async () => {
    setBrandBusy(true);
    try {
      let logo_payload = {};
      if (logoFile) {
        const buf = await logoFile.arrayBuffer();
        // btoa doesn't like binary; do chunked
        let bin = "";
        const bytes = new Uint8Array(buf);
        for (let i = 0; i < bytes.length; i += 8192) {
          bin += String.fromCharCode.apply(null, bytes.subarray(i, i + 8192));
        }
        logo_payload = {
          logo_base64: btoa(bin),
          logo_content_type: logoFile.type || "image/png",
        };
      }
      await siPutBranding(site._id, {
        brand_name: brand.brand_name || null,
        accent: brand.accent || null,
        ...logo_payload,
      });
      toast.success("Brand kit saved");
      setLogoFile(null);
      await loadBranding();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save brand kit");
    } finally { setBrandBusy(false); }
  };

  const clearLogo = async () => {
    setBrandBusy(true);
    try {
      await siPutBranding(site._id, { clear_logo: true });
      await loadBranding();
      toast.success("Logo cleared");
    } catch (e) {
      toast.error("Could not clear logo");
    } finally { setBrandBusy(false); }
  };

  useEffect(() => {
    if (brandOpen) loadBranding();
  }, [brandOpen, loadBranding]);

  const canDownload = progress?.status === "completed" || progress?.status === "cancelled";

  return (
    <Card className="border-slate-200 p-6" data-testid={`si-site-${site._id}`}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="font-heading text-xl font-bold text-slate-900">{site.label}</div>
          <div className="font-mono text-xs text-slate-500">{site.url}</div>
        </div>
        <div className="flex gap-2">
          {progress?.status === "running" || progress?.status === "queued" ? (
            <Button variant="outline" size="sm" onClick={cancel} data-testid="si-crawl-cancel">
              <Square className="mr-1 h-3.5 w-3.5" /> Cancel
            </Button>
          ) : (
            <Button size="sm" className="bg-orange-600 text-white hover:bg-orange-700" onClick={startCrawl} disabled={starting} data-testid="si-crawl-start">
              {starting ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" /> : <Play className="mr-1 h-3.5 w-3.5" />}
              Start crawl
            </Button>
          )}
        </div>
      </div>

      {site.latest_scores && (
        <div className="mt-4">
          <ScoreBlock scores={site.latest_scores} />
        </div>
      )}

      {progress && (
        <div className="mt-4 rounded-xl border border-slate-200 bg-slate-50 p-4" data-testid="si-crawl-progress">
          <div className="flex items-center justify-between text-xs font-semibold">
            <span className="uppercase tracking-wider text-slate-500">
              Crawl · {progress.status}
            </span>
            <span>{progress.progress || 0}%</span>
          </div>
          <div className="mt-2 h-2 overflow-hidden rounded-full bg-slate-200">
            <div className="h-full bg-orange-500 transition-all" style={{ width: `${progress.progress || 0}%` }} />
          </div>
          <div className="mt-2 text-xs text-slate-600">
            Pages found: <b>{progress.pages_found ?? 0}</b>
            {progress.issues_found != null && <> · Issues: <b>{progress.issues_found}</b></>}
          </div>
        </div>
      )}

      {issues && (
        <div className="mt-4" data-testid="si-issues">
          <div className="font-heading text-sm font-bold text-slate-900">Findings ({issues.count || 0})</div>
          {issues.grouped?.length > 0 ? (
            <div className="mt-2 flex flex-wrap gap-2">
              {issues.grouped.map((g) => (
                <span key={g.code} className={`rounded-full border px-3 py-1 text-xs ${SEVERITY_MAP[g.severity] || SEVERITY_MAP.info}`} data-testid={`si-finding-${g.code}`}>
                  {g.code.replace(/_/g, " ")} <b>×{g.count}</b>
                </span>
              ))}
            </div>
          ) : (
            <div className="mt-2 rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-xs text-emerald-800" data-testid="si-findings-clean">
              No findings on this crawl — every page passed the Phase 1 checks.
            </div>
          )}
        </div>
      )}

      {canDownload && (
        <div className="mt-4 rounded-xl border border-amber-200 bg-amber-50/40 p-4" data-testid="si-report-downloads">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <div className="font-heading text-sm font-bold text-slate-900">
                Download this report
              </div>
              <div className="text-xs text-slate-600">
                Branded PDF · raw CSV · white-label toggle + share links.
              </div>
            </div>
            <label className="flex items-center gap-2 text-xs font-semibold text-slate-700" data-testid="si-white-label-label">
              <input
                type="checkbox"
                checked={whiteLabel}
                onChange={(e) => setWhiteLabel(e.target.checked)}
                className="h-4 w-4 accent-orange-600"
                data-testid="si-white-label-toggle"
              />
              White-label (uses your Brand kit)
            </label>
          </div>
          <div className="mt-3 flex flex-wrap gap-2">
            <Button
              size="sm"
              onClick={() => downloadReport("pdf")}
              disabled={downloading === "pdf"}
              className="bg-orange-600 text-white hover:bg-orange-700"
              data-testid="si-download-pdf"
            >
              {downloading === "pdf"
                ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />
                : <FileDown className="mr-1 h-3.5 w-3.5" />}
              PDF report
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => downloadReport("csv")}
              disabled={downloading === "csv"}
              data-testid="si-download-csv"
            >
              {downloading === "csv"
                ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />
                : <Download className="mr-1 h-3.5 w-3.5" />}
              Raw CSV
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={createShareLink}
              disabled={sharing}
              data-testid="si-share-link"
            >
              {sharing
                ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />
                : <Link2 className="mr-1 h-3.5 w-3.5" />}
              Share public link
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => setBrandOpen(o => !o)}
              className="text-slate-700"
              data-testid="si-brand-kit-toggle"
            >
              <Palette className="mr-1 h-3.5 w-3.5" />
              {brandOpen ? "Hide brand kit" : "Brand kit"}
            </Button>
          </div>

          {shareLink && (
            <div className="mt-3 rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-xs" data-testid="si-share-result">
              <div className="font-semibold text-emerald-900">
                Public share link · expires {new Date(shareLink.expires_at).toLocaleDateString()}
              </div>
              <div className="mt-1 truncate font-mono text-[11px] text-emerald-800">
                {shareLink.share_url}
              </div>
              <Button
                size="sm"
                variant="ghost"
                className="mt-2 h-7 text-emerald-800"
                onClick={() => { navigator.clipboard.writeText(shareLink.share_url); toast.success("Copied"); }}
                data-testid="si-share-copy"
              >
                Copy link
              </Button>
            </div>
          )}

          {brandOpen && (
            <div className="mt-4 rounded-lg border border-slate-200 bg-white p-3" data-testid="si-brand-kit-panel">
              <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">Brand kit — used when white-label is on</div>
              <div className="mt-2 grid gap-3 sm:grid-cols-2">
                <label className="text-xs font-semibold text-slate-700">
                  Brand name
                  <Input
                    value={brand.brand_name}
                    onChange={(e) => setBrand({ ...brand, brand_name: e.target.value })}
                    placeholder="Acme SEO Co"
                    className="mt-1"
                    data-testid="si-brand-name"
                  />
                </label>
                <label className="text-xs font-semibold text-slate-700">
                  Accent color (hex)
                  <div className="mt-1 flex gap-2">
                    <Input
                      value={brand.accent}
                      onChange={(e) => setBrand({ ...brand, accent: e.target.value })}
                      placeholder="#0EA5E9"
                      className="font-mono"
                      data-testid="si-brand-accent"
                    />
                    {brand.accent && /^#[0-9a-fA-F]{3,6}$/.test(brand.accent) && (
                      <span
                        className="h-9 w-9 rounded border border-slate-200"
                        style={{ backgroundColor: brand.accent }}
                      />
                    )}
                  </div>
                </label>
                <label className="text-xs font-semibold text-slate-700 sm:col-span-2">
                  Logo (PNG/JPEG/SVG, max 2.5MB)
                  <div className="mt-1 flex flex-wrap items-center gap-3">
                    <input
                      type="file"
                      accept="image/png,image/jpeg,image/webp,image/svg+xml"
                      onChange={(e) => setLogoFile(e.target.files?.[0] || null)}
                      className="text-xs"
                      data-testid="si-brand-logo-file"
                    />
                    {brand.logo_url && !logoFile && (
                      <div className="flex items-center gap-2 rounded border border-slate-200 bg-slate-50 px-2 py-1">
                        <img src={brand.logo_url} alt="Current logo" className="h-8 max-w-[120px] object-contain" data-testid="si-brand-logo-current" />
                        <Button size="sm" variant="ghost" className="h-7 text-slate-500" onClick={clearLogo} disabled={brandBusy}>
                          <X className="h-3 w-3" /> Clear
                        </Button>
                      </div>
                    )}
                  </div>
                </label>
              </div>
              <div className="mt-3 flex gap-2">
                <Button
                  size="sm"
                  className="bg-slate-900 text-white hover:bg-slate-800"
                  onClick={saveBranding}
                  disabled={brandBusy}
                  data-testid="si-brand-save"
                >
                  {brandBusy
                    ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />
                    : <Check className="mr-1 h-3.5 w-3.5" />}
                  Save brand kit
                </Button>
              </div>
            </div>
          )}
        </div>
      )}

      {site.latest_crawl_id && (
        <div className="mt-6 rounded-xl border border-slate-200 bg-white p-4">
          <div className="flex items-center gap-2 font-heading text-sm font-bold text-slate-900">
            <MessageSquare className="h-4 w-4 text-orange-600" /> Ask the SEO Analyst
          </div>
          <Textarea
            data-testid="si-analyst-input"
            value={ask} onChange={e => setAsk(e.target.value)}
            placeholder="e.g. Which pages need the most urgent attention and why?"
            className="mt-2 min-h-[70px]"
          />
          <div className="mt-2 flex gap-2">
            <Button size="sm" onClick={askAnalyst} disabled={asking || !ask.trim()} className="bg-slate-900 text-white" data-testid="si-analyst-ask">
              {asking ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" /> : <Sparkles className="mr-1 h-3.5 w-3.5" />}
              Ask
            </Button>
          </div>
          {analyst?.answer && (
            <div className="mt-3 whitespace-pre-wrap rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm text-slate-800" data-testid="si-analyst-answer">
              {analyst.answer}
              {analyst.sources?.length > 0 && (
                <div className="mt-3 border-t border-slate-200 pt-2 text-xs text-slate-500">
                  Sources: {analyst.sources.slice(0, 3).map((s, i) => (
                    <span key={i} className="mr-3 font-mono">{s.url}</span>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </Card>
  );
};

export default function SearchIntelligenceConsole() {
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [newUrl, setNewUrl] = useState("");
  const [newLabel, setNewLabel] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await siListSites();
      setSites(r.sites || []);
    } catch (e) {
      toast.error("Failed to load sites");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const create = async () => {
    if (!newUrl.trim()) return;
    setCreating(true);
    try {
      await siCreateSite(newUrl.trim(), newLabel.trim() || undefined);
      setNewUrl(""); setNewLabel("");
      toast.success("Site added");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not add site");
    } finally { setCreating(false); }
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="si-console">
      <Navbar />
      <section className="border-b border-slate-200 bg-white">
        <div className="mx-auto max-w-6xl px-4 py-10">
          <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-orange-600">
            <Radar className="h-4 w-4" /> Search Intelligence · Console
          </div>
          <h1 className="mt-2 font-heading text-3xl font-black text-slate-900 sm:text-4xl">
            Your monitored sites
          </h1>
          <div className="mt-4 flex items-center gap-3">
            <Link to="/si/methodology" className="text-sm font-semibold text-orange-600 hover:underline" data-testid="si-console-method-link">
              Methodology <ArrowRight className="inline h-3 w-3" />
            </Link>
            <Link to="/si/sample" className="text-sm font-semibold text-slate-600 hover:underline">
              View sample report
            </Link>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-6xl px-4 py-8">
        <Card className="border-slate-200 bg-white p-6">
          <div className="font-heading text-lg font-bold text-slate-900">Add a site</div>
          <div className="mt-3 grid gap-2 sm:grid-cols-[1fr_1fr_auto]">
            <Input data-testid="si-add-url" value={newUrl} onChange={e => setNewUrl(e.target.value)} placeholder="https://example.com" />
            <Input data-testid="si-add-label" value={newLabel} onChange={e => setNewLabel(e.target.value)} placeholder="Label (optional)" />
            <Button data-testid="si-add-site" onClick={create} disabled={creating || !newUrl.trim()} className="bg-orange-600 text-white hover:bg-orange-700">
              {creating ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Plus className="mr-1 h-4 w-4" />}
              Add site
            </Button>
          </div>
        </Card>

        <div className="mt-8 space-y-6">
          {loading ? (
            <div className="text-slate-500">Loading…</div>
          ) : sites.length === 0 ? (
            <Card className="border-dashed border-slate-300 bg-white p-10 text-center text-slate-500" data-testid="si-empty">
              No sites yet. Add your first URL above.
            </Card>
          ) : (
            sites.map(s => <SitePanel key={s._id} site={s} onRefresh={load} />)
          )}
        </div>
      </section>
      <Footer />
    </div>
  );
}
